import React from "react";

interface IconProps {
  className?: string;
}

const STROKE = "#1F2937";

// Celular estilo line-art (contorno), ancho y con botón de inicio.
const PhoneOutline = () => (
  <>
    <rect
      x="19"
      y="11"
      width="62"
      height="98"
      rx="13"
      fill="#FFFFFF"
      stroke={STROKE}
      strokeWidth="4"
    />
    <line
      x1="27"
      y1="27"
      x2="73"
      y2="27"
      stroke={STROKE}
      strokeWidth="3"
      strokeLinecap="round"
    />
    <line
      x1="27"
      y1="92"
      x2="73"
      y2="92"
      stroke={STROKE}
      strokeWidth="3"
      strokeLinecap="round"
    />
    <circle
      cx="50"
      cy="100.5"
      r="3.6"
      fill="none"
      stroke={STROKE}
      strokeWidth="3"
    />
  </>
);

// Nuevo: celular + moneda con signo de peso/dólar (line-art).
export const NewFlowIcon = ({ className }: IconProps) => (
  <svg
    viewBox="0 0 120 120"
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    role="img"
    aria-label="Celular nuevo"
  >
    <PhoneOutline />
    <circle
      cx="88"
      cy="90"
      r="17"
      fill="#FFFFFF"
      stroke={STROKE}
      strokeWidth="3.5"
    />
    <text
      x="88"
      y="98"
      textAnchor="middle"
      fontFamily="Arial, Helvetica, sans-serif"
      fontSize="22"
      fontWeight="700"
      fill={STROKE}
    >
      $
    </text>
  </svg>
);

// Usado / reacondicionado: celular + flechas de renovación (line-art).
export const UsedFlowIcon = ({ className }: IconProps) => (
  <svg
    viewBox="0 0 120 120"
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    role="img"
    aria-label="Celular usado"
  >
    <PhoneOutline />
    <circle
      cx="88"
      cy="90"
      r="17"
      fill="#FFFFFF"
      stroke={STROKE}
      strokeWidth="3.5"
    />
    <g fill="none" stroke={STROKE} strokeWidth="2.6" strokeLinecap="round">
      <path d="M81 85 A 9.5 9.5 0 0 1 97 87" />
      <path d="M95 95 A 9.5 9.5 0 0 1 79 93" />
    </g>
    <path d="M97 81 l1.5 7.6 -7.6 -1.8 z" fill={STROKE} />
    <path d="M79 99 l-1.5 -7.6 7.6 1.8 z" fill={STROKE} />
  </svg>
);

// Beneficio: celular + regalo (line-art).
export const BenefitFlowIcon = ({ className }: IconProps) => (
  <svg
    viewBox="0 0 120 120"
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    role="img"
    aria-label="Celular con beneficio"
  >
    <PhoneOutline />
    <g
      fill="#FFFFFF"
      stroke={STROKE}
      strokeWidth="3"
      strokeLinejoin="round"
    >
      <rect x="67" y="87" width="38" height="21" rx="2" />
      <rect x="63" y="80" width="46" height="10" rx="2" />
    </g>
    <line
      x1="86"
      y1="81"
      x2="86"
      y2="108"
      stroke={STROKE}
      strokeWidth="3"
    />
    <path
      d="M86 79 C 79 72, 73 79, 86 81 C 99 79, 93 72, 86 79 Z"
      fill="#FFFFFF"
      stroke={STROKE}
      strokeWidth="2.6"
      strokeLinejoin="round"
    />
  </svg>
);

// Otros dispositivos electrónicos (TV/electrodomésticos): TV + base (line-art).
export const ApplianceFlowIcon = ({ className }: IconProps) => (
  <svg
    viewBox="0 0 120 120"
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    role="img"
    aria-label="Otros dispositivos electrónicos"
  >
    <rect
      x="14"
      y="24"
      width="92"
      height="60"
      rx="8"
      fill="#FFFFFF"
      stroke={STROKE}
      strokeWidth="4"
    />
    <rect
      x="24"
      y="34"
      width="72"
      height="40"
      rx="2"
      fill="none"
      stroke={STROKE}
      strokeWidth="2.6"
    />
    <line
      x1="60"
      y1="84"
      x2="60"
      y2="98"
      stroke={STROKE}
      strokeWidth="4"
      strokeLinecap="round"
    />
    <line
      x1="38"
      y1="102"
      x2="82"
      y2="102"
      stroke={STROKE}
      strokeWidth="4"
      strokeLinecap="round"
    />
  </svg>
);

// iPhone: cuerpo redondeado + notch (line-art), para el selector de MacCenter.
export const IPhoneFlowIcon = ({ className }: IconProps) => (
  <svg
    viewBox="0 0 120 120"
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    role="img"
    aria-label="iPhone"
  >
    <rect
      x="32"
      y="6"
      width="56"
      height="108"
      rx="14"
      fill="#FFFFFF"
      stroke={STROKE}
      strokeWidth="4"
    />
    <rect x="50" y="13" width="20" height="6" rx="3" fill={STROKE} />
  </svg>
);

// Mac (iMac): pantalla + cuello + base (line-art), para el selector de
// MacCenter ("otro dispositivo Apple": Mac/iPad/Apple Watch).
export const MacFlowIcon = ({ className }: IconProps) => (
  <svg
    viewBox="0 0 120 120"
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    role="img"
    aria-label="Mac"
  >
    <rect
      x="14"
      y="12"
      width="92"
      height="64"
      rx="10"
      fill="#FFFFFF"
      stroke={STROKE}
      strokeWidth="4"
    />
    <line
      x1="60"
      y1="76"
      x2="60"
      y2="96"
      stroke={STROKE}
      strokeWidth="4"
      strokeLinecap="round"
    />
    <path
      d="M38 108 C38 99, 82 99, 82 108"
      fill="none"
      stroke={STROKE}
      strokeWidth="4"
      strokeLinecap="round"
    />
  </svg>
);
